import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Formik } from "formik";
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
} from "@mui/material";
import { TextFieldCustom } from "../helpers/formFields";
import { getIconFromDriverName } from "./iconFunctions";
import { tooltip } from "../helpers/tooltip";
import { formatNumberThsndAndDec } from "../helpers/stringFormat";

// Utility to normalize the category/type label
const formatLabel = str =>
  str.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

export default function ChatEfficiencyDriverForm({
  category,
  type,
  multiple,
  frequency,
  driverKey,           // e.g. "operational_costs" or "personnel_costs"
  positiveDriver,      // true = savings/efficiency gain, false = extra cost
  iconName,
  driverConfig,
  suggestedJson = {},
  done = false,
  onSubmit            // (payload, { setSubmitting, setErrors }) => …
}) {
  const isOperational = Object.prototype.hasOwnProperty.call(driverConfig, driverKey);
  
  // Labels & units
  const gainLabel = isOperational
    ? `Efficiency ${positiveDriver ? "gain" : "loss"}`
    : `Hours ${positiveDriver ? "saved" : "extra"} per employee and month`;
  const gainUnit = isOperational ? "%" : "hours";
  const rangeLabel = "Range";
  const rangeUnit = gainUnit;

  // Compute initialValues from suggestedJson
  const initialValues = useMemo(() => {
    let mv = "0";
    let gain = "0";
    let range = isOperational ? "5" : "2";

    if (isOperational) {
      if (suggestedJson.cost_per_multiple != null) {
        // format with thousands separators for display
        mv = formatNumberThsndAndDec(
          suggestedJson.cost_per_multiple.toString()
        );
      }
      if (suggestedJson.efficiency_gain != null) {
        gain = suggestedJson.efficiency_gain.toString();
      }
      // if (suggestedJson.efficiency_gain?.range != null) {
      //   range = suggestedJson.efficiency_gain.range.toString();
      // }
    } else {
      if (suggestedJson.hours_saved?.value != null) {
        gain = suggestedJson.hours_saved.value.toString();
      }
      if (suggestedJson.hours_saved?.range != null) {
        range = suggestedJson.hours_saved.range.toString();
      }
    }

    return { cost_per_multiple: mv, gain, range };
  }, [suggestedJson, isOperational]);

  const tooltipText = {
    mv: `Cost per ${type} in the given period.`,
    efficiency: `Efficiency ${positiveDriver ? "gain" : "loss"} relative to competitive alternative.`,
    hours: `Hours ${positiveDriver ? "saved" : "extra"} per month per ${type}.`,
    range: `Range is how much the value may vary up or down.`,
  };

  return (
    <Formik
      key={JSON.stringify(suggestedJson)}     // force remount on suggestion change
      initialValues={initialValues}
      enableReinitialize={true}
      validate={values => {
        const errs = {};
        if (isOperational) {
          if (!values.cost_per_multiple || isNaN(values.cost_per_multiple.replace(/,/g, ""))) {
            errs.cost_per_multiple = "Required";
          }
        }
        if (!values.gain || isNaN(values.gain)) {
          errs.gain = "Required";
        }
        if (!values.range || isNaN(values.range)) {
          errs.range = "Required";
        }
        return errs;
      }}
      onSubmit={(values, { setSubmitting, setErrors }) => {
        const gainNum = parseFloat(values.gain);
        const rangeNum = parseFloat(values.range);
        const low = Math.max(0, gainNum - rangeNum);
        const high = isOperational ? Math.min(100, gainNum + rangeNum) : gainNum + rangeNum;

        const payload = {
          category,
          type,
          driver: driverKey,
          positive_driver: positiveDriver,
          range: rangeNum,
          low,
          high,
        };

        if (isOperational) {
          // strip commas
          const mvClean = parseFloat(values.cost_per_multiple.replace(/,/g, ""));
          payload.cost_per_multiple = mvClean;
          payload.efficiency_gain = gainNum;
        } else {
          payload.hours_saved = gainNum;
        }

        onSubmit(payload, { setSubmitting, setErrors });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting
      }) => (
        <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600 }}>
          <Grid container spacing={2} alignItems="stretch">
            {/* Category & Type */}
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                <strong>{formatLabel(category)}</strong>: {formatLabel(type)}
              </Typography>
            </Grid>

            {/* cost_per_multiple only for operational */}
            {isOperational && (
              <Grid item xs={12}>
                <Typography variant="caption" gutterBottom>
                  Cost per {multiple}{tooltip(tooltipText.mv)}
                </Typography>
                <TextFieldCustom
                  name="cost_per_multiple"
                  value={values.cost_per_multiple}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.cost_per_multiple && Boolean(errors.cost_per_multiple)}
                  helperText={touched.cost_per_multiple && errors.cost_per_multiple}
                  startAdornment="€"
                  endAdornment={frequency === 'year'? <Typography variant="caption">per year</Typography>: null}
                  fullWidth
                  disabled={!done}
                />
              </Grid>
            )}

            {/* Gain / Hours Saved */}
            <Grid item xs={6}>
              <Typography variant="caption" gutterBottom>
                {gainLabel}
                {tooltip(isOperational ? tooltipText.efficiency : tooltipText.hours)}
              </Typography>
              <TextFieldCustom
                name="gain"
                value={values.gain}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.gain && Boolean(errors.gain)}
                helperText={touched.gain && errors.gain}
                endAdornment={<Typography variant="caption">{gainUnit}</Typography>}
                fullWidth
                greenBackground={positiveDriver}
                redBackground={!positiveDriver}
                disabled={!done}
              />
            </Grid>

            {/* Range */}
            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <Typography variant="caption" gutterBottom>
                {rangeLabel}{tooltip(tooltipText.range)}
              </Typography>
              <TextFieldCustom
                name="range"
                type="number"
                value={values.range}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.range && Boolean(errors.range)}
                helperText={touched.range && errors.range}
                startAdornment={
                  <Stack
                    direction="column"
                    alignItems="center"
                    justifyContent="center"
                    spacing={0}
                  >
                    <Typography variant="caption" sx={{ lineHeight: 1 }}>+</Typography>
                    <Typography variant="caption" sx={{ lineHeight: 1 }}>−</Typography>
                  </Stack>
                }
                endAdornment={<Typography variant="caption">{rangeUnit}</Typography>}
                fullWidth
                disabled={!done}
              />
            </Grid>

            {/* Submit button */}
            <Grid item xs={12} sx={{ textAlign: "right" }}>
              <Button
                variant="contained"
                startIcon={<Box component={getIconFromDriverName(iconName)} />}
                disabled={isSubmitting || !done}
                type="submit"
                size="small"
                sx={{ textTransform: "none" }}
              >
                {isSubmitting ? "Saving…" : "Add Driver"}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
    </Formik>
  );
}

ChatEfficiencyDriverForm.propTypes = {
  category: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  multiple: PropTypes.string,
  driverKey: PropTypes.string.isRequired,
  positiveDriver: PropTypes.bool.isRequired,
  iconName: PropTypes.string.isRequired,
  driverConfig: PropTypes.object.isRequired,
  suggestedJson: PropTypes.object,
  done: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};
